import { getTranslation } from 'helpers/getTranslation';

import {
  hasLessonNameInProperties,
  isLessonRelatedEventKey,
} from './lessonTypeIcons';
import {
  isModuleContextEventKey,
  resolveModuleFlowKind,
} from './moduleTypeIcons';

const readNonEmptyString = (value: unknown): string | null =>
  typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;

export type CatalogContextDisplay = {
  icon: string | null;
  /** Course cover from the catalog (`module_preview` / `course_preview`, filled by admin-api); used instead of `icon` when set. */
  showCoursePreview: boolean;
  coursePreview: string | null;
  courseLine: string | null;
  moduleLine: string | null;
  lessonName: string | null;
};

/** admin-api marks module ids it resolved from `coursemodules` (a module inside a course). */
const isCourseModule = (properties: Record<string, unknown> | undefined) =>
  properties?.module_kind === 'course_module';

/**
 * Name of the top catalog item (trainer / training / arcade / course) the event
 * belongs to. Course modules resolve to their course (`course_name`); the module
 * itself goes to the «Модуль: …» line.
 */
export const resolveCatalogParentLabel = (
  properties: Record<string, unknown> | undefined,
): string | null => {
  if (!properties) {
    return null;
  }

  const moduleName = readNonEmptyString(properties.module_name);
  const courseName = readNonEmptyString(properties.course_name);
  const trainingName = readNonEmptyString(properties.training_name);
  const arcadeName = readNonEmptyString(properties.arcade_name);
  const flowKind = resolveModuleFlowKind(properties);

  if (flowKind === 'training') {
    return trainingName ?? moduleName;
  }

  if (flowKind === 'arcade') {
    return arcadeName ?? moduleName;
  }

  if (flowKind === 'course' || isCourseModule(properties)) {
    return courseName ?? moduleName;
  }

  return moduleName ?? trainingName ?? arcadeName ?? courseName;
};

/**
 * Catalog titles are Russian in both UI languages, so both spellings count:
 * «Модуль 2. Сетки» already says it is a module.
 */
const MODULE_TITLE_WORDS = ['модуль', 'module'];

/** «Модуль 2. Сетки» — yes; «Модульная сетка» — no (the word goes on). */
const startsWithModuleWord = (name: string): boolean => {
  const normalizedName = name.toLocaleLowerCase();

  return MODULE_TITLE_WORDS.some(
    (word) =>
      normalizedName.startsWith(word) &&
      !/^[a-zа-яё]/i.test(normalizedName.slice(word.length)),
  );
};

/**
 * Course-module title for the «Модуль: …» line, when it differs from the
 * course line. A title that starts with «Модуль» is shown as is, not as
 * «Модуль: Модуль 2. …».
 */
const resolveModuleLine = (
  properties: Record<string, unknown> | undefined,
  courseLine: string | null,
): string | null => {
  const moduleName = readNonEmptyString(properties?.module_name);

  if (!moduleName || !courseLine || moduleName === courseLine) {
    return null;
  }

  if (startsWithModuleWord(moduleName)) {
    return moduleName;
  }

  return getTranslation('student_page.timeline.catalog.module_line', {
    name: moduleName,
  });
};

const resolveCoursePreview = (
  properties: Record<string, unknown> | undefined,
) => {
  const modulePreview = readNonEmptyString(properties?.module_preview);
  const coursePreview = readNonEmptyString(properties?.course_preview);

  return isCourseModule(properties)
    ? coursePreview ?? modulePreview
    : modulePreview ?? coursePreview;
};

/** Course / module / lesson hierarchy for journey nodes — icon + stacked labels. */
export const resolveCatalogContext = ({
  translationKey,
  properties,
  moduleLabel,
  lessonLabel,
  moduleTypeIcon,
  lessonTypeIcon,
}: {
  translationKey: string;
  properties?: Record<string, unknown>;
  moduleLabel: string | null;
  lessonLabel: string | null;
  moduleTypeIcon: string | null;
  lessonTypeIcon: string | null;
}): CatalogContextDisplay | null => {
  const parentName = resolveCatalogParentLabel(properties);
  const showForLesson =
    Boolean(lessonLabel) &&
    (isLessonRelatedEventKey(translationKey) ||
      hasLessonNameInProperties(properties));
  const showForModuleOnly =
    !lessonLabel &&
    Boolean(moduleLabel) &&
    (isModuleContextEventKey(translationKey) || Boolean(parentName));

  if (!showForLesson && !showForModuleOnly) {
    return null;
  }

  /** Catalog titles already carry the kind ("Тренажер. …"), so no flow-kind prefix. */
  const courseLine = parentName ?? moduleLabel;
  const moduleLine = resolveModuleLine(properties, courseLine);
  const lessonName = showForLesson ? lessonLabel : null;

  if (!courseLine && !moduleLine && !lessonName) {
    return null;
  }

  return {
    icon: moduleTypeIcon ?? lessonTypeIcon,
    showCoursePreview: Boolean(courseLine),
    coursePreview: resolveCoursePreview(properties),
    courseLine,
    moduleLine,
    lessonName,
  };
};

/**
 * The block without the lines its chapter header already says: in a course
 * chapter a lesson row keeps only «Модуль: …», in a trainer chapter only the
 * lesson name. `null` when nothing is left.
 */
export const omitCatalogContextLines = (
  catalogContext: CatalogContextDisplay | null,
  omit: { course?: boolean; lesson?: boolean },
): CatalogContextDisplay | null => {
  if (!catalogContext) {
    return null;
  }

  const courseLine = omit.course ? null : catalogContext.courseLine;
  const lessonName = omit.lesson ? null : catalogContext.lessonName;

  if (!courseLine && !catalogContext.moduleLine && !lessonName) {
    return null;
  }

  return {
    ...catalogContext,
    /* The course cover belongs to the course line. */
    showCoursePreview: catalogContext.showCoursePreview && Boolean(courseLine),
    courseLine,
    lessonName,
  };
};
